/* src/components/projects/RenderFields.jsx */
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Input,
  Slider,
  Switch,
  Select,
  InputNumber,
  Tooltip,
  Button,
  Space,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { getRequest } from "../../util/service";
import FileUploadField from "./FileUploadField";

const { Option } = Select;

/* ── Slider configuration (unchanged) ─────────────────────────────────────── */
const sliders = {
  clogD_limit:               { min: -20, max: 50,  step: 0.1, keys: ["clogD_l_limit", "clogD_u_limit"] },
  pKa_limit:                 { min: -20, max: 50,  step: 0.1, keys: ["pKa_llimit", "pKa_ulimit"] },
  chiral_centers:            { min:   0, max: 10,  step: 1,   keys: ["w_nchiral_0", "w_nchiral_1"] },
  ring_size:                 { min:   0, max: 10,  step: 1,   keys: ["w_lrsz_0",   "w_lrsz_1"] },
  number_of_rings:           { min:   0, max: 10,  step: 1,   keys: ["w_nrings_0", "w_nrings_1"] },
  number_of_aromatic_rings:  { min:   0, max: 10,  step: 1,   keys: ["w_arrings_0","w_arrings_1"] },
  mW:                        { min:   0, max:1000, step: 1,   keys: ["w_amw_0",    "w_amw_1"] },
  rotatable_bonds:           { min:   0, max: 10,  step: 1,   keys: ["w_rotbond_0","w_rotbond_1"] },
  halogens:                  { min:   0, max: 10,  step: 1,   keys: ["w_halogen_0","w_halogen_1"] },
  OH_NH:                     { min:   0, max: 10,  step: 1,   keys: ["w_ro5_ohnh_0","w_ro5_ohnh_1"] },
  polar_surface_area:        { min:   0, max:150,  step: 1,   keys: ["w_nvrtspsa_0","w_nvrtspsa_1"] },
  H_bond_donors:             { min:   0, max: 10,  step: 1,   keys: ["w_hdons_0",  "w_hdons_1"] },
  H_bond_acceptors:          { min:   0, max: 10,  step: 1,   keys: ["w_hacts_0",  "w_hacts_1"] },
};

const FILE_MAP = {
  blocks_file: {
    fileType: "Blocksfiles",
    accept: ".json",
    maxSizeMB: 10,
    predefOptions: [
      { value: "Chemblly_all.json",              label: "ChEMBL + Lilly Fragments" },
      { value: "Chemblly_all_large_blocks.json", label: "ChEMBL + Lilly Fragments (large)" },
    ],
  },
  ref_compounds: {
    fileType: "Referencefiles",
    accept: ".smi,.sdf",
    maxSizeMB: 10,
  },
  SMARTS_file: {
    fileType: "Smartsfiles",
    accept: ".txt",
    maxSizeMB: 10,
  },
};

const FIELDS_TO_FREEZE_ON_RETRIGGER = new Set([
  "blocks_file",
  "nemb",
  "num_conv_steps",
  "model_version",
  "reward_mode",
]);

export default function RenderFields({
  projectId,
  fields,
  formData,
  displayField,
  handleInputChange,
  handleSlider,
  handleSigmoidValue,
  isReTriggerMode,
}) {
  // Fragmenter outfiles to merge into "Fragment building blocks" (blocks_file) dropdown
  const [fileGroups, setFileGroups] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const fetchAndGroupFiles = useCallback(async () => {
    if (!projectId) return;
    setFilesLoading(true);
    try {
      const [blocksResponse, fragmentsResponse] = await Promise.all([
        getRequest(`/riva/moleculegenbatches/${projectId}/Blocksfiles`),
        getRequest(`/riva/moleculegenbatches/${projectId}/FragmenterOutfiles`)
      ]);

      const blocksData = blocksResponse.data || [];
      const defaultFiles = blocksData.find(d => d.common_files)?.common_files.filter(Boolean) || [];
      const userUploadedFiles = blocksData.find(d => d.files)?.files.filter(Boolean) || [];
      
      const fragmentsData = fragmentsResponse.data || [];
      const processGeneratedFiles = fragmentsData.find(d => d.files)?.files.filter(Boolean) || [];

      const groups = [
        { label: "Default", options: defaultFiles.map(f => ({ label: f, value: f, type: "Blocksfiles" })) },
        { label: "User Uploaded", options: userUploadedFiles.map(f => ({ label: f, value: f, type: "Blocksfiles", deletable: true })) },
        { label: "Process-Generated", options: processGeneratedFiles.map(f => ({ label: f, value: f, type: "FragmenterOutfiles" })) }
      ];
      setFileGroups(groups);
    } catch (err) {
      setFileGroups([]);
    } finally {
      setFilesLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => {
    if (fields.some(f => f.label === "blocks_file")) {
      fetchAndGroupFiles();
    }
  }, [fields, fetchAndGroupFiles]);

  const handleOpenFragmentation = useCallback(() => {
    if (!projectId) return;
    const fragmentationUrl = new URL(`/${projectId}/fragmentation`, window.location.origin).toString();
    window.open(fragmentationUrl, "_blank", "noopener,noreferrer");
  }, [projectId]);

  
  return (
     <Row gutter={[16, 16]} align="middle">
      {fields.map((f) => {
        if (!displayField(f.label)) return null;
        const isDisabled = (isReTriggerMode && FIELDS_TO_FREEZE_ON_RETRIGGER.has(f.label)) || f.disabled || false;

        // const disabled = isReTriggerMode || f.disabled || false;
        const { span, mb, labelSize, labelWeight, helper, alias, required } = {
            span: f.span ?? (sliders[f.label] || f.type === "file" ? 8 : 4),
            mb: f.mb ?? 8,
            labelSize: f.labelSize ?? 13,
            labelWeight: f.labelWeight ?? 500,
            disabled: f.disabled ?? false,
            helper: f.helperText,
            alias: f.alias,
            required: f.required
        };
        const labelStyle = { display: "block", marginBottom: 2, fontSize: labelSize, fontWeight: labelWeight };

        // ✅ MODIFIED: All file logic is now inside this single block.
        if (f.type === "file") {
          const cfg = FILE_MAP[f.label];
          if (!cfg) return null;

          const isBlocksFile = f.label === "blocks_file";
          const containerSpan = isBlocksFile ? 10 : span;
          const fieldWidthPercent = isBlocksFile ? `${(span / containerSpan) * 100}%` : "100%";

          const handleFilePick = (selectedValue) => {
            if (isBlocksFile) {
                const allOptions = fileGroups.flatMap(g => g.options);
                const selectedOption = allOptions.find(opt => opt.value === selectedValue);
                
                handleInputChange({ target: { name: f.label, value: selectedValue } });
                handleInputChange({
                  target: {
                    name: `${f.label}_type`,
                    value: selectedOption?.type || "Blocksfiles",
                  },
                });
            } else {
                // Handle other file types (e.g., SMARTS_file)
                handleInputChange({ target: { name: f.label, value: selectedValue } });
            }
          };

          return (
            <Col span={containerSpan} key={f.label} style={{ marginBottom: mb }}>
              <label style={labelStyle}>
                {alias}
                {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
                {helper && <Tooltip title={helper}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}
              </label>
              <div
                style={
                  isBlocksFile
                    ? {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }
                    : undefined
                }
              >
                <div
                  style={
                    isBlocksFile
                      ? {
                          width: fieldWidthPercent,
                          minWidth: fieldWidthPercent,
                          flexShrink: 0,
                        }
                      : undefined
                  }
                >
              <FileUploadField
                projectId={projectId}
                fileType={cfg.fileType}
                value={formData[f.label]}
                onChange={handleFilePick}
                accept={cfg.accept}
                maxSizeMB={cfg.maxSizeMB}
                // Pass the correct options based on the file type
                optionGroups={isBlocksFile ? fileGroups : null}
                predefOptions={!isBlocksFile ? cfg.predefOptions : []}
                loading={isBlocksFile ? filesLoading : undefined}
               disabled={isDisabled}
                 onReload={isBlocksFile ? fetchAndGroupFiles : undefined}
              />
                </div>
                {f.label === "ref_compounds" && (
                  <div style={{ width: "100%", color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>
                    Supported formats: .smi, .sdf
                  </div>
                )}
                {isBlocksFile && (
                  <Space size="small">
                    <Button
                      type="primary"
                      onClick={handleOpenFragmentation}
                    >
                      Fragmentation
                    </Button>
                  </Space>
                )}
              </div>
            </Col>
          );
        }
        /* ==================== slider ==================== */
        if (sliders[f.label]) {
          const cfg        = sliders[f.label];
          const [lo, hi]   = cfg.keys.map((k) => formData[k] ?? 0);
          return (
            <Col span={span} key={f.label} style={{ marginBottom: mb }}>
              <label style={labelStyle}>
                {f.alias}
                {f.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
                {helper && <Tooltip title={helper}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}
              </label>
              <Slider
                range
               disabled={isDisabled}
                min={cfg.min}
                max={cfg.max}
                step={cfg.step}
                value={[lo, hi]}
                onChange={(v) => handleSlider(f.label, v)}
              />
            </Col>
          );
        }

        /* ==================== toggle ==================== */
        if (f.type === "toggle") {
          return (
            <Col span={span} key={f.label} style={{ marginBottom: mb }}>
              <label style={labelStyle}>
                {f.alias}
                {f.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
                {helper && <Tooltip title={helper}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}
              </label>
              <Switch
                checked={formData[f.label]}
                disabled={isDisabled}
                onChange={(v) =>
                  handleInputChange({ target: { name: f.label, value: v } })
                }
                checkedChildren="True"
                unCheckedChildren="False"
              />
            </Col>
          );
        }

        /* ============ sigmoid quartet (unchanged) ============ */
        if (f.label === "docking_reward_sigmoid_params") {
          const [a, b, c, d] =
            formData.docking_reward_sigmoid_params || [1, 0, 0, -1];
          const fields = ["a", "b", "c", "d"];
          return (
            <Col span={span} key={f.label} style={{ marginBottom: mb }}>
              <label style={labelStyle}>
                {f.alias}
                {f.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
                {helper && <Tooltip title={helper}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}
              </label>
              <div style={{ display: "flex", gap: 4 }}>
                {fields.map((lbl, idx) => (
                  <Input
                    key={lbl}
                    disabled={f.disabled ?? false}
                    placeholder={lbl}
                    value={[a, b, c, d][idx]}
                    onChange={(e) => handleSigmoidValue(lbl, e)}
                    style={{ flex: 1 }}
                  />
                ))}
              </div>
            </Col>
          );
        }

         if (f.label === "blocks_file") {
          const handleFilePick = (selectedValue) => {
            // The FileUploadField now passes back the full option object
            const selectedOption = fileGroups.flatMap(g => g.options).find(opt => opt.value === selectedValue);
            
            handleInputChange({ target: { name: f.label, value: selectedValue } });
            // Set the file type based on the group it came from
            if (selectedOption) {
                handleInputChange({ target: { name: `${f.label}_type`, value: selectedOption.type } });
            }
          };
              return (
                      <Col span={8} key={f.label} style={{ marginBottom: 8 }}>
                        <label style={{ display: "block", marginBottom: 2, fontSize: 13 }}>
                          {f.alias}
                          {f.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
                        </label>
                        <FileUploadField
                          projectId={projectId}
                          fileType="Blocksfiles"
                          value={formData[f.label]}
                          onChange={handleFilePick}
                          accept=".json"
                          maxSizeMB={10}
                          // ✅ NEW: Pass the structured groups instead of separate lists
                          optionGroups={fileGroups}
                          loading={filesLoading}
                        />
                      </Col>
                    );
                  }

        /* ==================== select ==================== */
        if (f.type === "select") {
          return (
            <Col span={span} key={f.label} style={{ marginBottom: mb }}>
              <label style={labelStyle}>
                {f.alias}
                {f.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
                {helper && <Tooltip title={helper}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}
              </label>
              <Select
                disabled={isDisabled}
                style={{ width: "100%" }}
                value={formData[f.label]}
                onChange={(val) =>
                  handleInputChange({ target: { name: f.label, value: val } })
                }
                placeholder={`Select ${f.alias}`}
              >
                {f.options?.map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt}
                  </Option>
                ))}
              </Select>
            </Col>
          );
        }

        /* ==================== text / number ==================== */
        const isIterationsField = f.label === "num_iterations";
        const showIterationWarning =
          isIterationsField && Number(formData[f.label]) > 15000;
        const unitAddon =
          f.label === "microsomal_limit"
            ? "uL/min/mg"
            : f.label === "mdck_limit"
              ? (
                  <span>
                    log10(<span style={{ fontSize: "1.05em" }}>r</span><sub style={{ fontSize: "0.72em" }}>MDCK</sub>)
                  </span>
                )
              : undefined;

        return (
          <Col span={span} key={f.label} style={{ marginBottom: mb }}>
            <label style={labelStyle}>
              {f.alias}
              {f.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
              {helper && <Tooltip title={helper}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}
            </label>
            {f.type === "number" ? (
              <div
                style={
                  isIterationsField
                    ? {
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                      }
                    : undefined
                }
              >
                <div style={isIterationsField ? { width: 160, flexShrink: 0 } : { width: "100%" }}>
                  <InputNumber
                    disabled={f.disabled ?? false}
                    style={{ width: "100%" }}
                    min={f.minvalue}
                    max={f.maxvalue}
                    step={f.step}
                    addonAfter={unitAddon}
                    value={formData[f.label]}
                    onChange={(v) =>
                      handleInputChange({ target: { name: f.label, value: v } })
                    }
                  />
                </div>
                {isIterationsField && (
                  <span
                    style={{
                      color: "#cf1322",
                      fontStyle: "italic",
                      fontSize: 12,
                      lineHeight: 1.4,
                      flex: 1,
                      minWidth: 0,
                      visibility: showIterationWarning ? "visible" : "hidden",
                    }}
                  >
                    Higher iteration counts result in greater AWS time and cost expenditure.
                  </span>
                )}
              </div>
            ) : (
              <Input
                disabled={f.disabled ?? false}
                style={{ width: "100%" }}
                value={formData[f.label] ?? ""}
                onChange={(e) => handleInputChange(e)}
                placeholder={f.alias}
              />
            )}
          </Col>
        );
      })}
    </Row>
  );
}

RenderFields.propTypes = {
  projectId:            PropTypes.string.isRequired,
  fields:               PropTypes.array.isRequired,
  formData:             PropTypes.object.isRequired,
  displayField:         PropTypes.func.isRequired,
  handleInputChange:    PropTypes.func.isRequired,
  handleSlider:         PropTypes.func.isRequired,
  handleSigmoidValue:   PropTypes.func.isRequired,
  isReTriggerMode:      PropTypes.bool,
};
